/*
 * Application State should be all within this file.
 */

// Elevation values are Z-values (meters?) from LIDAR point-cloud
// const TRUE_GPS_LOCATIONS = {
//   PI: { lat: 44.56647582, lon: -123.2714859, elevation: 70.118 },
//   TC: {
//     lat: 44.56633430257188,
//     lon: -123.2724271618623,
//     elevation: 70.98
//   }
// };
import { sections, PointName, CurveConstants } from "@/data/content";
import { Vector3 } from "three";
import { SIGHT_HEIGHT } from "@/helpers/constants";
import { metersToFeet } from "@/helpers/surveyingUtils";

/**
 * Decorates sections with completion status of steps
 * @param {Section[]} sections - Static sections with steps
 * @param {Object<string, boolean>} [progress] - Optional map of step id to completion status
 * @returns {Section[]} - Sections with completion status of steps
 */
const decorateSections = (sections, progress) => {
  return sections.map((section) => {
    return {
      ...section,
      steps: section.steps?.map((step) => ({
        ...step,
        completed: progress?.[step.id] ?? false
      }))
    };
  });
};

/**
 *
 * @param {Section[]} sections - Sections with completion status of steps
 * @returns {Object<string, boolean>} - map of step id to completion status
 */
const extractSectionProgress = (sections) => {
  return sections.reduce((acc, section) => {
    section.steps.forEach((step) => {
      acc[step.id] = step.completed;
    });
    return acc;
  }, {});
};

const app = {
  state: {
    showDataTable: false,
    atPointName: PointName.PI,
    sections: [],
    // Instrument height is no longer being entered by user
    enteredInstrumentHeight: metersToFeet(SIGHT_HEIGHT),
    // The section of the activity that the user is currently on
    currentSectionIndex: 0,
    // position of stakes placed by user
    pointPositions: {
      [PointName.PI]: new Vector3(0, 0, 0),
      [PointName.TC]: null,
      [PointName.CT]: null,
      [PointName.TP1]: null,
      [PointName.TP2]: null,
      [PointName.TP3]: null,
      [PointName.TP4]: null,
      [PointName.TP5]: null,
      [PointName.MP]: null,
      [PointName.CT2]: null,
      // For now, back tangent marker should be 1.5*T, directly west of PI
      // TODO: move this initialization to the scene mixin, once it involves a more complex calculation
      // TODO: determine if this point should be a stake or some other type of marker
      [PointName.BACK_TANGENT_MARKER]: new Vector3(
        CurveConstants.T * -1.5,
        0,
        0
      )
    },
    // Only relevant for smartphone screen sizes
    minimizeSidebar: false,
    showControlInstructionsModal: false,
    // Whether the user has made a measurement with the total station on the current step
    hasMeasuredOnCurrentStep: false,
    errorMessage: ""
  },
  mutations: {
    SET_SECTIONS(state, value) {
      state.sections = value;
    },
    /**
     * Updates the completion status of a single step by its section index and step index
     * This is the legacy approach to updating step completion status
     * @deprecated
     * @param {Object} state
     * @param {{sectionIndex: number, stepIndex: number, completed: boolean}} payload
     */
    UPDATE_STEP_COMPLETED_BY_INDEX(state, payload) {
      const { sectionIndex, stepIndex, completed } = payload;
      state.sections[payload.sectionIndex].steps[payload.stepIndex] = {
        ...state.sections[sectionIndex].steps[stepIndex],
        completed
      };
    },
    /**
     * Updates the completion status of a single step by its id
     * This is the preferred approach to updating step completion status in new development
     * @param {Object} state
     * @param {{stepId: string, completed: boolean}} payload
     */
    UPDATE_STEP_COMPLETED_BY_ID(state, payload) {
      const { stepId, completed } = payload;
      state.sections.forEach((section) => {
        section.steps.forEach((step) => {
          if (step.id === stepId) {
            step.completed = completed;
          }
        });
      });
    },
    UPDATE_POINT_POSITIONS(state, payload) {
      state.pointPositions = payload;
    },
    UPDATE_POINT_POSITION(state, payload) {
      state.pointPositions[payload.pointName] = payload.position;
    },
    UPDATE_INSTRUMENT_HEIGHT(state, height) {
      state.enteredInstrumentHeight = height;
    },
    SET_AT_POINT_NAME(state, pointName) {
      state.atPointName = pointName;
    },
    SET_CURRENT_SECTION_INDEX(state, index) {
      state.currentSectionIndex = index;
      if (index > 0) {
        // all sections after the first are at point TC
        state.atPointName = PointName.TC;
      } else {
        // first section is at point PI
        state.atPointName = PointName.PI;
      }
    },
    TOGGLE_SIDEBAR(state) {
      state.minimizeSidebar = !state.minimizeSidebar;
    },
    SET_SHOW_DATA_TABLE(state, value) {
      state.showDataTable = value;
    },
    SET_SHOW_CONTROL_INSTRUCTIONS_MODAL(state, value) {
      state.showControlInstructionsModal = value;
    },
    SET_HAS_MEASURED_ON_CURRENT_STEP(state, value) {
      state.hasMeasuredOnCurrentStep = value;
    },
    SET_ERROR_MESSAGE(state, value) {
      state.errorMessage = value;
    }
  },
  actions: {
    toggleDataTable({ commit, state }) {
      commit("SET_SHOW_DATA_TABLE", !state.showDataTable);
    },
    mapFromDatastore({ commit, state }, payload) {
      /*
       * This is called whenever the Datastore/entry changes.
       * This is an opportunity for you to map stored values
       * back into your state.
       */
      let _payload = "";
      try {
        _payload = JSON.parse(payload);
      } catch (e) {
        _payload = payload;
      }

      commit(
        "SET_SECTIONS",
        decorateSections(sections, _payload?.sectionProgress)
      );
      commit(
        "UPDATE_POINT_POSITIONS",
        _payload?.pointPositions ?? state.pointPositions
      );
      commit(
        "SET_CURRENT_SECTION_INDEX",
        _payload?.currentSectionIndex ?? state.currentSectionIndex
      );
    },
    /**
     * Updates the completion status of a single step
     * @param {{commit: function}} param0
     * @param {{sectionIndex: number, stepIndex: number, completed: boolean}} payload
     */
    updateStepCompletedByIndex({ commit }, payload) {
      commit("UPDATE_STEP_COMPLETED_BY_INDEX", payload);
    },
    /**
     * Updates the completion status of a single step by its id
     * @param {{commit: function}} param0
     * @param {{stepId: string, completed: boolean}} payload
     */
    updateStepCompletedByID({ commit }, payload) {
      commit("UPDATE_STEP_COMPLETED_BY_ID", payload);
    },
    /**
     * Updates the position of a single stake
     * @param {{commit: function}} param0
     * @param {{pointName: string, position: Vector3 | null}} payload
     */
    updatePointPosition({ commit }, payload) {
      commit("UPDATE_POINT_POSITION", payload);
    },
    updateInstrumentHeight({ commit }, height) {
      commit("UPDATE_INSTRUMENT_HEIGHT", height);
    },
    setAtPointName({ commit }, pointName) {
      commit("SET_AT_POINT_NAME", pointName);
    },
    setCurrentSectionIndex({ commit }, index) {
      commit("SET_CURRENT_SECTION_INDEX", index);
    },
    toggleSidebar({ commit }) {
      commit("TOGGLE_SIDEBAR");
    },
    toggleControlInstructionsModal({ commit, state }) {
      commit(
        "SET_SHOW_CONTROL_INSTRUCTIONS_MODAL",
        !state.showControlInstructionsModal
      );
    },
    closeControlInstructionsModal({ commit }) {
      commit("SET_SHOW_CONTROL_INSTRUCTIONS_MODAL", false);
    },
    setHasMeasuredOnCurrentStep({ commit }, value) {
      commit("SET_HAS_MEASURED_ON_CURRENT_STEP", value);
    },
    setErrorMessage({ commit }, value) {
      commit("SET_ERROR_MESSAGE", value);
    }
  },
  getters: {
    getShowDataTable(state) {
      return state.showDataTable;
    },
    getSections(state) {
      return state.sections;
    },
    mapToDatastore(state) {
      /*
       * Datastore Payload is only used whenever you're using the
       * Datastore (or Datasheets). This is an object
       */

      const sectionProgress = extractSectionProgress(state.sections);
      let payload = {
        pointPositions: state.pointPositions,
        sectionProgress,
        currentSectionIndex: state.currentSectionIndex
      };

      payload = JSON.stringify(payload);
      return payload;
    },
    getPointPositions(state) {
      return state.pointPositions;
    },
    getAtPointName(state) {
      // Return default at point name if not set by point selection
      return state.atPointName;
    },
    getCurrentStepId: function (state) {
      // Find the first step that is not completed
      for (const section of state.sections) {
        for (const step of section.steps) {
          if (!step.completed) {
            return step.id;
          }
        }
      }
      return null;
    },
    getInstrumentHeight(state) {
      return state.enteredInstrumentHeight;
    },
    getCurrentSectionIndex(state) {
      return state.currentSectionIndex;
    },
    getMinimizeSidebar(state) {
      return state.minimizeSidebar;
    },
    getFlattenedSteps(state) {
      return state.sections.flatMap((section) => section.steps);
    },
    getShowControlInstructionsModal(state) {
      return state.showControlInstructionsModal;
    },
    getHasMeasuredOnCurrentStep(state) {
      return state.hasMeasuredOnCurrentStep;
    },
    getErrorMessage(state) {
      return state.errorMessage;
    }
  }
};

export default app;
